import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, Table, Group, Menu } from '@mantine/core';
import Header from '../../components/util/header';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import PaginationComponent from '../../components/pagination';
import { ICurrency } from '../../../libraries/currencies';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { fetchUsersSummaries, fetchUserSummaryXLSReport, IUserSummary } from '../../../libraries/brisa';
import { formatCurrAmount, formatTransactionType } from '../../utils/script';
import { formatDateTimeLabel } from '../../utils/date';
import AddEditUserSummary from './edit-user-summary';
import { isErrorResponse, isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import './styles.scss';

const UserSummaries = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const region = useSelector<AppState, string>(state => state.login.user?.region || 'UTC');
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = useRef(new AbortController()).current;
  const navigate = useNavigate();

  const [totalRows, setTotalRows] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [userSummaries, setUserSummaries] = useState<IUserSummary[]>([]);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const [contextEntid, setContextEntid] = useState<number | null>(null);
  const [contextUid, setContextUid] = useState<number | null>(null);

  const [menuOpened, setMenuOpened] = useState(false);
  const [menuCoords, setMenuCoords] = useState<{ x: number; y: number } | null>(null);

  const [modalOpened, setModalOpened] = useState(false);
  const [selectedUserSummary, setSelectedUserSummary] = useState<IUserSummary | null>(null);

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadTransactionList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadTransactionList = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setUserSummaries([]);
    setTotalRows(0);

    fetchUsersSummaries(filterBarData.entity || 0, offset, limit, true, filterBarData.uid, filterBarData.balance)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setUserSummaries(response?.data?.list || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRowRightClick = (event: React.MouseEvent, userSummary: IUserSummary) => {
    event.preventDefault();

    setSelectedUserSummary(userSummary);

    setContextEntid(userSummary.entid);
    setContextUid(userSummary.uid);

    const menuWidth = 200;
    const menuHeight = 300;
    const maxX = window.innerWidth - menuWidth;
    const maxY = window.innerHeight - menuHeight;

    const x = event.clientX > maxX ? maxX : event.clientX;
    const y = event.clientY > maxY ? maxY : event.clientY;

    setMenuCoords({ x, y });
    setMenuOpened(true);
  };

  //----------------------------------------------------------------//

  const getXLSReport = async () => {
    if (!filterBarData) return;

    try {
      const response = await fetchUserSummaryXLSReport(
        filterBarData.entity || 0,
        controller,
        filterBarData.balance || true
      );

      if (isErrorResponse(response)) {
        handleApiError(response, lang.TRANSACTIONS_XLS_ERROR);
        return;
      }
    } catch (err: any) {
      handleApiError(err, lang.TRANSACTIONS_XLS_ERROR);
    }
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  return (
    <div className="user-summaries">
      <AddEditUserSummary
        opened={modalOpened}
        close={() => setModalOpened(false)}
        userSummary={selectedUserSummary ?? undefined}
        mode="edit"
        onSaved={() => {
          loadTransactionList();
        }}
      />

      <Header pageTitle={lang.BRISA_USERS_SUMMARIES_TITLE} />

      <FilterBar
        onDataChange={handleFilterBarOnDataChange}
        onSearch={loadTransactionList}
        fieldsUsage={{
          entity: true,
          name: true,
          uid: true,
          balance: true
        }}
        xlsReport={userSummaries.length ? getXLSReport : undefined}
      />

      <div ref={tableWrapperRef}>
        <Table.ScrollContainer minWidth={500} ref={tableRef}>
          <Table striped verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{lang.BRISA_USERS_SUMMARIES_ID}</Table.Th>
                <Table.Th>{lang.BRISA_USERS_SUMMARIES_NAME}</Table.Th>
                <Table.Th>{lang.BRISA_USERS_SUMMARIES_LAST_OPER}</Table.Th>
                <Table.Th>{lang.BRISA_USERS_SUMMARIES_EID_LAST_OPER}</Table.Th>
                <Table.Th>{lang.BRISA_USERS_SUMMARIES_DATE_LAST_OPER}</Table.Th>
                <Table.Th>{lang.BRISA_USERS_SUMMARIES_AMOUNT_LAST_OPER}</Table.Th>
                <Table.Th>{lang.BRISA_USERS_SUMMARIES_BALANCE}</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={16}>
                    <Loader color="#016273" />
                  </Table.Td>
                </Table.Tr>
              ) : (
                <>
                  {!userSummaries.length ? (
                    <Table.Tr>
                      <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                        {lang.GLOBAL_NO_RESULTS}
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    userSummaries?.map((userSummary, index) => {
                      const matchingCurrency = currencies?.find(c => c.cid === userSummary?.cid);

                      return (
                        <Table.Tr key={index} onContextMenu={event => handleRowRightClick(event, userSummary)}>
                          <Table.Td>{userSummary.uid}</Table.Td>
                          <Table.Td>{userSummary.name}</Table.Td>
                          <Table.Td>{formatTransactionType(lang, userSummary.lastOperation)}</Table.Td>
                          <Table.Td>{userSummary.lastOperationEid}</Table.Td>
                          <Table.Td>{formatDateTimeLabel(userSummary.lastOperationDt, region)}</Table.Td>
                          <Table.Td>{formatCurrAmount(userSummary.lastOperationValue, matchingCurrency)}</Table.Td>
                          <Table.Td>{formatCurrAmount(userSummary.balance, matchingCurrency)}</Table.Td>
                        </Table.Tr>
                      );
                    })
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>

      {menuOpened && menuCoords && (
        <Menu
          opened={true}
          onClose={() => setMenuOpened(false)}
          styles={{
            dropdown: {
              position: 'absolute',
              top: `${menuCoords.y}px`,
              left: `${menuCoords.x}px`
            }
          }}
        >
          <Menu.Target>
            <div
              style={{
                position: 'absolute',
                top: `${menuCoords.y}px`,
                left: `${menuCoords.x}px`,
                width: 1,
                height: 1
              }}
            />
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              onClick={() => {
                setMenuOpened(false);
                setModalOpened(true);
              }}
            >
              {lang.BRISA_USERS_SUMMARIES_MENU_FIX_BALANCE}
            </Menu.Item>

            <Menu.Item onClick={() => navigate(`/transactions/${contextEntid}/${contextUid}`)}>
              {lang.BRISA_USERS_SUMMARIES_MENU_FIX_TRANSACTIONS}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      )}

      <Group>
        <PaginationComponent
          total={totalRows}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default UserSummaries;
